// Catalog.ID Client — Derive-Root Keystore
// ============================================================================
// The side-effecting half of the `derive` keybag op: mint-or-adopt the
// account's derive root and persist it as an ordinary key-bag entry
// (`derive_root`), so the EXISTING sync machinery replicates it to every
// device of the account — which is what makes every device derive identical
// keys (EFS lineage-labels §5b).
//
// Kept out of `derive-handlers.ts` so the gate logic there stays unit-testable
// with no network and no modals — the same split `ceremony-keystore.ts` uses
// for the A8b ceremony.
//
// The supersecret prompt is mandatory and IS the consent for this step: the
// Argon2id key is re-derived per bag mutation and zeroed on use (audit M12),
// so a stray derive request cannot silently add a durable account secret. A
// user who dismisses the prompt gets `supersecret-required`, which the handler
// reports as `denied`, not as a failure.
//
// ── The one invariant this file exists to hold ──────────────────────────────
// **The root a device uses is the one the SERVER holds.** Never our own
// freshly-minted bytes on faith. `POST /api/account/keybag/entries` is
// last-writer-wins (`ON CONFLICT … DO UPDATE`), so two devices minting
// concurrently would otherwise each walk away believing in a different root —
// and every labeled KEK derived from the loser's root would be
// unreproducible anywhere else. Two defences, in order:
//
//   1. Look at the server bag BEFORE minting. The common case — a second
//      device, or any device whose in-memory bag predates the mint — adopts
//      and writes nothing.
//   2. Read back after writing and return whatever the server now holds. A
//      concurrent write that landed in between wins, and we adopt it.
//
// That leaves one residual window (a competing write that lands strictly
// between our POST and our read-back), which the API's lack of a
// compare-and-set cannot close from here. It is orders of magnitude narrower
// than the naive mint-and-trust, and it fails toward "adopt someone else's
// root", never toward "use a root nobody else has".
// ============================================================================

import type { Shell } from '@catalog-shell';
import * as accountApi from '../api/account';
import * as aes from '../crypto/aes';
import { decryptEntry, encryptEntry, type EncryptedKeyBagEntry } from '../crypto/keybag';
import { keys } from '../state/keys';
import { promptAndDeriveKey } from './supersecret-prompt';
import { signStepUp } from '../auth/step-up';
import {
    DERIVE_ROOT_KEY_ID, DERIVE_ROOT_ALGORITHM, DERIVE_ROOT_BYTES, generateDeriveRoot,
} from '../crypto/derive';

/** The single generation this slot has; rotation is an estate sweep, not a bump. */
const DERIVE_ROOT_KEY_INDEX = 0;

/**
 * Return the account's derive root, minting and persisting one if the account
 * has none. Always returns the root the server holds.
 *
 * @throws Error('supersecret-required') when the user dismisses the prompt.
 */
export async function ensureDeriveRoot(shell: Shell): Promise<Uint8Array> {
    const serverBag = await accountApi.getKeyBag();
    const verifyAgainst = serverBag.entries.find(e => e.status !== 'revoked')
        ?? serverBag.entries[0]
        ?? null;

    const result = await promptAndDeriveKey(shell, {
        saltB64: serverBag.keySalt,
        verifyAgainst,
        title: 'Set up encrypted-storage key derivation',
        description:
            'Your Catalog.ID needs a one-time derivation secret so every device of this '
            + 'account computes the same storage keys. It is generated here, stored in your '
            + 'key bag, and never leaves it. Enter your supersecret to encrypt it; the '
            + 'supersecret is used only to derive the encryption key, then discarded.',
    });
    if (!result) throw new Error('supersecret-required');

    const derivedKey = result.derivedKey;
    try {
        // Defence 1: adopt whatever is already there.
        const existing = await readServerRoot(serverBag.entries, derivedKey);
        if (existing) return adopt(existing);

        const minted = generateDeriveRoot();
        const encrypted = await encryptEntry({
            keyId: DERIVE_ROOT_KEY_ID,
            algorithm: DERIVE_ROOT_ALGORITHM,
            privateKey: aes.toBase64(minted),
            keyIndex: DERIVE_ROOT_KEY_INDEX,
            status: 'active',
        }, derivedKey);

        // The single-entry add is step-up gated (§10 item 7). Signed HERE,
        // immediately before the POST, because the challenge is single-use and
        // short-lived: obtaining it earlier would burn one on the adopt path,
        // which writes nothing at all. `main_signing_key` is in the bag on
        // every path that reaches this line — the prompt above just opened it.
        const challengeSignature = await signStepUp('keybag-entry');
        await accountApi.addKeyBagEntry({
            keyId: encrypted.keyId,
            keyIndex: encrypted.keyIndex,
            algorithm: encrypted.algorithm,
            encryptedPrivateKey: encrypted.encryptedPrivateKey,
            nonce: encrypted.nonce,
            status: encrypted.status,
        }, challengeSignature);

        // Defence 2: read back. What the server holds is the account's root,
        // whether or not it is what we just sent.
        const readBack = await readServerRoot(
            (await accountApi.getKeyBag()).entries, derivedKey);
        if (!readBack) {
            throw new Error(
                'derive root did not survive the write — the key bag holds no '
                + `'${DERIVE_ROOT_KEY_ID}' entry after persisting it`);
        }
        return adopt(readBack);
    } finally {
        derivedKey.fill(0);
    }
}

/**
 * Mirror the root into the in-memory bag so the derive that triggered this can
 * proceed in the same round trip (a reload-to-use hop would strand the caller's
 * build), and return it.
 */
function adopt(root: Uint8Array): Uint8Array {
    keys.addEntry({
        keyId: DERIVE_ROOT_KEY_ID,
        algorithm: DERIVE_ROOT_ALGORITHM,
        privateKey: aes.toBase64(root),
        keyIndex: DERIVE_ROOT_KEY_INDEX,
        status: 'active',
    });
    return root;
}

/**
 * The decrypted `derive_root` from a server bag response, or null when the
 * account has none. A present-but-wrong-sized root throws rather than being
 * silently used: deriving from it would produce keys no other device could
 * reproduce, which is the exact failure this whole design exists to prevent.
 */
async function readServerRoot(
    entries: EncryptedKeyBagEntry[],
    derivedKey: Uint8Array,
): Promise<Uint8Array | null> {
    const row = entries.find(
        e => e.keyId === DERIVE_ROOT_KEY_ID && (e.keyIndex ?? 0) === DERIVE_ROOT_KEY_INDEX);
    if (!row) return null;

    const opened = await decryptEntry(row, derivedKey);
    const root = aes.fromBase64(opened.privateKey);
    if (root.length !== DERIVE_ROOT_BYTES) {
        throw new Error(
            `stored derive root is ${root.length} bytes; expected ${DERIVE_ROOT_BYTES}`);
    }
    return root;
}
